import { Tour, PricingContextState } from '../types';

/**
 * Centralized filtering utility for tours.
 * Implements strict filtering and fallback "closest match" logic.
 */
export function filterTours(tours: Tour[], context: PricingContextState): Tour[] {
    if (!tours || tours.length === 0) return [];

    const {
        searchQuery,
        category,
        duration,
        difficulty,
        budgetRange,
        // tier - Not strictly used for initial filtering, handled by pricing context later
    } = context;

    // 1. Initial Strict Filter
    let filtered = tours.filter(tour => {
        // Master rule: Never show deactivated tours
        if (tour.status === 'deactivated') return false;

        // Search Query
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const matchesSearch =
                tour.title?.toLowerCase().includes(query) ||
                tour.description?.toLowerCase().includes(query) ||
                tour.location?.name?.toLowerCase().includes(query) ||
                tour.category?.toLowerCase().includes(query);
            if (!matchesSearch) return false;
        }

        // Category
        if (category && category !== 'All') {
            if (tour.category?.toLowerCase() !== category.toLowerCase()) return false;
        }

        // Duration
        if (duration && duration !== 'Any Duration') {
            const tourDurationStr = tour.duration || '0';
            const tourDuration = parseInt(tourDurationStr.split(' ')[0] || '0');

            if (duration === '1-3 Days' && (tourDuration < 1 || tourDuration > 3)) return false;
            if (duration === '4-7 Days' && (tourDuration < 4 || tourDuration > 7)) return false;
            if (duration === '8-14 Days' && (tourDuration < 8 || tourDuration > 14)) return false;
            if (duration === '15+ Days' && tourDuration < 15) return false;
        }

        // Difficulty
        if (difficulty && difficulty !== 'Any Level') {
            const tourDifficulty = (tour as any).difficulty || 'Moderate';
            if (tourDifficulty.toLowerCase() !== difficulty.toLowerCase()) return false;
        }

        // Budget Range (Base price check)
        if (budgetRange) {
            const tourPrice = tour.price || 0;
            // Note: This is an approximation since final price depends on context, 
            // but it's good for initial filtering.
            if (tourPrice < budgetRange.min || tourPrice > budgetRange.max) return false;
        }

        return true;
    });

    // 2. "Closest Match" Logic if no strict results found
    if (filtered.length === 0) {
        console.log('🔍 No strict matches found, applying closest match logic...');

        // Relax Budget first
        filtered = tours.filter(tour => {
            if (category && category !== 'All' && tour.category?.toLowerCase() !== category.toLowerCase()) return false;
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                return tour.title?.toLowerCase().includes(query) || tour.location?.name?.toLowerCase().includes(query);
            }
            return true;
        });

        // Sort by relevance to query if search was used
        if (searchQuery && filtered.length > 0) {
            const query = searchQuery.toLowerCase();
            filtered.sort((a, b) => {
                const aTitle = a.title.toLowerCase();
                const bTitle = b.title.toLowerCase();
                if (aTitle.includes(query) && !bTitle.includes(query)) return -1;
                if (!aTitle.includes(query) && bTitle.includes(query)) return 1;
                return 0;
            });
        }
    }

    // 3. Absolute Fallback: Never return empty if we have tours
    if (filtered.length === 0 && tours.length > 0) {
        console.log('⚠️ No close matches found, returning top rated tours as fallback');
        filtered = [...tours].sort((a, b) => (b.rating || 0) - (a.rating || 0)).slice(0, 6);
    }

    return filtered;
}
